import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import {
  Row,
  Col,
  Button,
  ProgressBar,
  Card,
  Badge,
  Spinner,
  Container,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import * as Yup from "yup";
import { base_url } from "../utils/axiosConfig";
import axios from "axios";

const RetailerOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [retailer, setRetailer] = useState(null);
  const [stockData, setStockData] = useState([]);

  const initialValues = {
    fullName: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    email: "",
    mobile: "",
    address_line: "",
    city: "",
    district: "",
    state: "",
    country: "",
    pincode: "",
    profilePhoto: null,
    mainProductId: "",
    verticalProductId: "",
    confirmation: false,
  };

  // Validation Schemas (unchanged)
  const userValidationSchema = Yup.object().shape({
    fullName: Yup.string()
      .required("Full name is required")
      .min(2, "Full name must be at least 2 characters"),
    dob: Yup.date().max(new Date(), "Date of birth cannot be in the future"),
    gender: Yup.string().oneOf(["Male", "Female", "Other"]),
    bloodGroup: Yup.string().oneOf([
      "A+",
      "A-",
      "B+",
      "B-",
      "AB+",
      "AB-",
      "O+",
      "O-",
    ]),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
    address_line: Yup.string().required("Address line is required"),
    city: Yup.string().required("City is required"),
    district: Yup.string().required("District is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    profilePhoto: Yup.mixed()
      .nullable()
      .test("fileSize", "File size must be less than 2MB", (value) => {
        if (!value) return true; // allow null/undefined
        return value.size <= 2 * 1024 * 1024;
      })
      .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
        if (!value) return true; // allow null/undefined
        return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
      }),
  });

  const productValidationSchema = Yup.object().shape({
    mainProductId: Yup.string().required("Main product is required"),
    verticalProductId: Yup.string().required("Vertical product is required"),
    confirmation: Yup.boolean().oneOf(
      [true],
      "You must confirm the customer onboarding and payment",
    ),
  });

  const combinedValidationSchema = (step) => {
    return step === 1 ? userValidationSchema : productValidationSchema;
  };

  const handleNext = (values, setFieldValue, setSubmitting) => {
    if (step === 2) {
      const stock = stockData.find(
        (s) => s.verticalProductId === values.verticalProductId,
      );
      if (!stock || stock.stock < 1) {
        toast.error("Selected product is out of stock");
        setSubmitting(false);
        return;
      }
    }
    setStep((prev) => prev + 1);
    setSubmitting(false);
  };

  const handleBack = () => setStep((prev) => prev - 1);

  const handleSubmit = async (values, { setSubmitting }) => {
    // setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("fullName", values.fullName);
      formData.append("dob", values.dob);
      formData.append("gender", values.gender);
      formData.append("bloodGroup", values.bloodGroup);
      formData.append("email", values.email);
      formData.append("mobile", values.mobile);
      formData.append("address[address_line]", values.address_line);
      formData.append("address[city]", values.city);
      formData.append("address[district]", values.district);
      formData.append("address[state]", values.state);
      formData.append("address[country]", values.country);
      formData.append("address[pincode]", values.pincode);
      formData.append("profilePhoto", values.profilePhoto);
      formData.append("mainProductId", values.mainProductId);
      formData.append("verticalProductId", values.verticalProductId);
      formData.append("amount", values.amount);
      formData.append("retailerId", id);
      formData.append("amountWithTax", values.amountWithTax);
      formData.append("planType", retailer.retailerType);
      const response = await axios.post(
        `${base_url}v1/user/productSellByRetailerQrCode`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 201) {
        toast.success("Order created successfully");
        if (retailer.retailerType === "free") {
          navigate("/");
        } else {
          const { paymentSessionId } = response.data;

          if (!paymentSessionId) {
            throw new Error("No payment session received from server");
          }

          // 3. Start Cashfree checkout
          const cashfree = new window.Cashfree();

          cashfree
            .checkout({
              paymentSessionId,
              redirectTarget: "_self",
              mode: "production", // change to "sandbox" during testing
            })
            .catch((err) => {
              console.error("Cashfree checkout failed:", err);
              toast.error("Failed to open payment gateway. Please try again.");
            });
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create order";
      console.log(errorMessage);
      toast.error(errorMessage);
    } finally {
      // setIsLoading(false);
      setSubmitting(false);
    }
  };

  const fetchRetailerById = async () => {
    try {
      const response = await axios.get(
        `${base_url}product/fetchRetailer/${id}`,
      );
      setRetailer(response.data.data);
      setStockData(response.data.stockData || []);
    } catch (error) {
      console.error("Error fetching retailer:", error);
      toast.error("Failed to load retailer information");
      navigate("/");
    }
  };

  useEffect(() => {
    if (id) fetchRetailerById();
  }, [id]);

  return (
    <Container className="py-5">
      {/* Header + Retailer Info */}
      <div className="mb-5">
        <h2 className="fw-bold mb-1">Create Customer Order</h2>
        <p className="text-muted">
          Retailer ID: <strong>{id}</strong>
        </p>

        {retailer && (
          <Card className="retailer-info-card mt-3">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <strong>Retailer Name:</strong>{" "}
                  {retailer.name || retailer.fullName || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong>{" "}
                  {retailer.mobile || retailer.phone || "N/A"}
                </Col>
                {retailer.email && (
                  <Col md={6} className="mt-2">
                    <strong>Email:</strong> {retailer.email}
                  </Col>
                )}
                {retailer.city && (
                  <Col md={6} className="mt-2">
                    <strong>Location:</strong> {retailer.city}, {retailer.state}
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>

      <ProgressBar
        now={(step / 2) * 100}
        className="mb-5 custom-progress"
        style={{ height: "10px", borderRadius: "10px" }}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={combinedValidationSchema(step)}
        onSubmit={(values, { setSubmitting, setFieldValue }) => {
          if (step < 2) {
            handleNext(values, setFieldValue, setSubmitting);
          } else {
            const stock = stockData.find(
              (s) => s.verticalProductId === values.verticalProductId,
            );
            const amount = stock?.pricePerUnit || 0;
            const amountWithTax = amount + Number((amount * 0.18).toFixed(2));
            handleSubmit(
              { ...values, amount, amountWithTax },
              { setSubmitting },
            );
          }
        }}
      >
        {({ handleSubmit, setFieldValue, isSubmitting, values }) => (
          <Form onSubmit={handleSubmit}>
            {/* Step 1: Customer Details */}
            {step === 1 && (
              <div className="form-step">
                <h4 className="mb-4 text-primary">Customer Information</h4>
                <Row className="g-4">
                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="fullName"
                      className="form-control form-control-lg"
                      placeholder="Enter full name"
                    />
                    <ErrorMessage
                      name="fullName"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Date of Birth
                    </label>
                    <Field
                      type="date"
                      name="dob"
                      className="form-control form-control-lg"
                      max={moment().format("YYYY-MM-DD")}
                    />
                    <ErrorMessage
                      name="dob"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">Gender</label>
                    <Field
                      as="select"
                      name="gender"
                      className="form-select form-select-lg"
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Field>
                    <ErrorMessage
                      name="gender"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Blood Group{" "}
                    </label>
                    <Field
                      as="select"
                      name="bloodGroup"
                      className="form-select form-select-lg"
                    >
                      <option value="">Select blood group</option>
                      {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                        (bg) => (
                          <option key={bg} value={bg}>
                            {bg}
                          </option>
                        ),
                      )}
                    </Field>
                    <ErrorMessage
                      name="bloodGroup"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Email <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className="form-control form-control-lg"
                      placeholder="Enter email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="mobile"
                      className="form-control form-control-lg"
                      placeholder="Enter 10-digit mobile"
                    />
                    <ErrorMessage
                      name="mobile"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  {/* Address Fields */}
                  {[
                    "address_line",
                    "city",
                    "district",
                    "state",
                    "country",
                    "pincode",
                  ].map((field) => (
                    <Col md={6} key={field}>
                      <label className="form-label fw-semibold capitalize">
                        {field.replace("_", " ")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        name={field}
                        className="form-control form-control-lg"
                        placeholder={`Enter ${field.replace("_", " ")}`}
                      />
                      <ErrorMessage
                        name={field}
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Col>
                  ))}

                  <Col md={12}>
                    <label className="form-label fw-semibold">
                      Profile Photo
                    </label>
                    <input
                      type="file"
                      className="form-control form-control-lg"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={(e) =>
                        setFieldValue("profilePhoto", e.currentTarget.files[0])
                      }
                    />
                    <ErrorMessage
                      name="profilePhoto"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>
                </Row>
              </div>
            )}

            {/* Step 2: Product Selection */}
            {step === 2 && (
              <div className="form-step">
                <h4 className="mb-4 text-primary">Select Product</h4>

                {/* Main Product */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Main Product <span className="text-danger">*</span>
                  </label>
                  <Field
                    as="select"
                    name="mainProductId"
                    className="form-select form-select-lg"
                    onChange={(e) => {
                      setFieldValue("mainProductId", e.target.value);
                      setFieldValue("verticalProductId", "");
                    }}
                  >
                    <option value="">Select Main Product</option>
                    {[
                      ...new Set(stockData.map((s) => s.mainProductId._id)),
                    ].map((pid) => {
                      const prod = stockData.find(
                        (s) => s.mainProductId._id === pid,
                      )?.mainProductId;
                      return (
                        <option key={pid} value={pid}>
                          {prod?.mainProductName}
                        </option>
                      );
                    })}
                  </Field>
                  <ErrorMessage
                    name="mainProductId"
                    component="div"
                    className="text-danger mt-1"
                  />
                </div>

                {/* Product Cards */}
                {values.mainProductId && (
                  <Row className="g-4 mb-5">
                    {stockData
                      .filter(
                        (stock) =>
                          stock.mainProductId._id === values.mainProductId,
                      )
                      .map((stock) => {
                        const product =
                          stock.mainProductId.verticalProducts.find(
                            (vp) => vp._id === stock.verticalProductId,
                          );
                        const isOutOfStock = stock.stock === 0;
                        const isSelected =
                          values.verticalProductId === stock.verticalProductId;

                        return (
                          <Col lg={6} key={stock.verticalProductId}>
                            <Card
                              className={`product-card h-100 ${isSelected ? "selected" : ""} ${isOutOfStock ? "out-of-stock" : ""}`}
                            >
                              <Card.Img
                                variant="top"
                                src={
                                  product?.images?.[0] ||
                                  "https://via.placeholder.com/300x200"
                                }
                                className="product-image"
                              />
                              <Card.Body>
                                <Card.Title className="fw-bold">
                                  {product?.productName}
                                </Card.Title>
                                <Card.Text className="text-muted small mb-3">
                                  {product?.briefDescription ||
                                    "No description available"}
                                </Card.Text>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div className="fs-4 fw-bold text-success">
                                    ₹{stock.pricePerUnit}
                                  </div>
                                  <Badge
                                    bg={isOutOfStock ? "danger" : "success"}
                                    pill
                                  >
                                    Stock: {stock.stock}
                                  </Badge>
                                </div>

                                {!isOutOfStock && (
                                  <div className="form-check">
                                    <Field
                                      type="radio"
                                      name="verticalProductId"
                                      value={stock.verticalProductId}
                                      className="form-check-input"
                                      id={`prod-${stock.verticalProductId}`}
                                    />
                                    <label
                                      className="form-check-label fw-semibold"
                                      htmlFor={`prod-${stock.verticalProductId}`}
                                    >
                                      Select this product
                                    </label>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                  </Row>
                )}

                {/* Price Summary */}
                {values.verticalProductId && (
                  <div className="price-summary p-4 rounded-3 mb-4">
                    {/* ... your existing price summary code ... */}
                  </div>
                )}

                {/* Confirmation */}
                <div className="confirmation-box p-4 rounded-3">
                  <div className="form-check">
                    <Field
                      type="checkbox"
                      name="confirmation"
                      className="form-check-input"
                      id="confirmation"
                    />
                    <label
                      htmlFor="confirmation"
                      className="form-check-label fw-semibold"
                    >
                      I confirm that the customer has been onboarded, payment
                      received, and details entered correctly.
                    </label>
                  </div>
                  <ErrorMessage
                    name="confirmation"
                    component="div"
                    className="text-danger mt-2"
                  />
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="d-flex justify-content-between mt-5 pt-4 border-top">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={step === 1 ? () => window.history.back() : handleBack}
              >
                {step === 1 ? "Cancel" : "← Back"}
              </Button>

              <Button
                variant="success"
                size="lg"
                type="submit"
                disabled={isSubmitting}
                className="px-5"
                style={{ backgroundColor: "#02BE0F", borderColor: "#02BE0F" }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {step === 2 ? "Submitting..." : "Next"}
                  </>
                ) : step === 2 ? (
                  "Submit Order"
                ) : (
                  "Continue →"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default RetailerOrder;
